import log from "loglevel";
import useNotifier from "./Notifier";
import { AxiosError, isAxiosError } from "axios";
import { useAPILogger } from "@/main";
import DateTimeSv from "@/types/DateTimeSv";

export interface INotifyError {
    /** What caused this error */
    errorType?: string,
    /** Additional Msg */
    msg?: string,
    /** From which class and function */
    name?: string,
    /** Error obj */
    error: unknown,
}
export interface IError {
    Error: string;
}
export default function useNotifierWithErrFormatter() {
    const notifier = useNotifier();
    const _error = notifier.error;
    function isIError(data): data is IError {
        return !!data?.Error;
    }
    async function error(props: INotifyError) {
        log.error(props.error);
        
        const bServerSide = isAxiosError(props.error) ? 
            props.error.response?.status?.toString().startsWith('5') : false;
        const is400 = isAxiosError(props.error) ? props.error.response?.status === 400 : false;
        const is401 = isAxiosError(props.error) ? props.error.response?.status === 401 : false;
        const is403 = isAxiosError(props.error) ? props.error.response?.status === 403 : false;
        const is424 = isAxiosError(props.error) ? props.error.response?.status === 424 : false;
        const is404 = isAxiosError(props.error) ? props.error.response?.status === 404 : false;
        
        const errTitle = is400 ? 'Warning' : 'Error';
        const title = props.errorType ? `${new DateTimeSv()} - ${errTitle} (${props.errorType})` : undefined;
        const errTitle404 = new DateTimeSv() + " - " + errTitle + " (Env URL missing)"
        const serverOrClient1 =  bServerSide ? 'server-side' : 'client';
        const serverOrClient2 = bServerSide ? '' : ' (an error with your request)';
        const errorBaseText = `\n\n${props.error}`;
        const data = (props.error as AxiosError).response?.data;
        const errorExtraText = (is400 || is424 ) && isIError(data) ? data.Error : undefined;
        const errorText = errorExtraText ? `${errorBaseText}\n${errorExtraText}` : errorBaseText;
        const baseMsg = props.msg ? `\n${props.msg}` : '';
        const msgError = `Your request could not be fulfilled at this moment due to a ${serverOrClient1} error${serverOrClient2}, please try again later. If this error persists, please raise a ticket with the help desk.${errorText}${baseMsg}`;
        const msgWarn = `Your request could not be fulfilled at this moment due to a ${serverOrClient1} error${serverOrClient2}. Please try again with different parameters. If this error persists, please raise a ticket with the help desk.${errorText}${baseMsg}`;
        const msg424 = `Your request could not be fulfilled at this moment due to a failed dependency. If this error persists, please raise a ticket with help desk.${errorText}${baseMsg}`;
        const msg404 = "The request could not be completed because a required environment URL variable is missing. Please contact the support team or raise a help desk ticket." +  errorText + baseMsg;
        const msg401 = "You are not authorized to perform this action. Please, contact help desk if you think you should have access"; 
        const msg403 = "Your account is forbidden to perform this action. Please, contact help desk if you think you should have access";
        useAPILogger().LogError(`${title} - ${is400? msgWarn : msgError}`, props.name);
        if (is400) return notifier.warn({ title, msg:msgWarn, dur: 0 });
        if (is424) return notifier.warn({ title, msg:msg424, dur: 0 });
        if (is404) return notifier.error({ title: errTitle404, msg: msg404 })
        if (is401) return notifier.warn({ title, msg:msg401, dur: 0});
        if (is403) return notifier.warn({ title, msg:msg403, dur: 0});
        return _error({ title,msg: msgError });
    }
    return {success: notifier.success, warn: notifier.warn, notify: notifier.notify, error};
}